import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContextSelector } from '@/redux';
import {
  ContextMenu,
  DefaultNumberSorting,
  Table,
  TableColumnType,
  TabPanel,
  useDefaultTablePaging,
  useSorting,
} from '@/components';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArticleLookupState, useSearchArticlesQuery } from '@/redux/articles';
import {
  AddArticleButton,
  useDeleteArticleAction,
  useUpdateArticleStatusAction,
} from '../Articles.Details';
import { ArticleStatusBadge } from './ArticleStatusBadge';
import { ArticleStatus } from '@/core/http.articles';
import { ArticlesSearchBar } from './ArticleSearchBar';
import { useFiltersFactory } from '@/utils';

function useColumns(
  openEdit: (id: string) => any,
  confirmDelete: (id: string) => any,
  confirmPublish: (id: string) => any,
  confirmHide: (id: string) => any,
) {
  const hasRole = useUserContextSelector((x) => x.hasRole);

  const { t } = useTranslation();
  const canModify = hasRole('BFP_ADMIN');

  const filters = useFiltersFactory<ArticleLookupState>([]);

  return useMemo<TableColumnType<ArticleLookupState>[]>(() => {
    const columns: Array<TableColumnType<ArticleLookupState> | false> = [
      {
        title: t('articles.details.number'),
        key: 'number',
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.number.localeCompare(b.number),
        render: (_, item) => item.number,
      },
      {
        title: t('articles.details.title'),
        key: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        render: (_, { id, title }) => (
          <NavLink to={`/admin/articles/${id}`}>{title}</NavLink>
        ),
      },
      {
        title: t('articles.details.status'),
        key: 'status',
        ...filters.enumSelect((x) => x.status, ArticleStatus),
        sorter: (a, b) => a.status.localeCompare(b.status),
        render: (_, item) => <ArticleStatusBadge value={item} />,
      },
      canModify && {
        title: t('articles.details.actions'),
        key: 'actions',
        render: (_, { id, status }) => (
          <ContextMenu
            items={[
              {
                onClick: () => openEdit(id),
                label: t('articles.action.edit'),
              },
              {
                onClick: () => confirmDelete(id),
                label: t('articles.action.delete'),
              },
              {
                onClick: () => confirmPublish(id),
                label: t('articles.action.publish'),
                if: status !== ArticleStatus.Published,
              },
              {
                onClick: () => confirmHide(id),
                label: t('articles.action.hide'),
                if: status === ArticleStatus.Published,
              },
            ]}
          />
        ),
      },
    ];
    return columns.filter((x) => !!x) as TableColumnType<ArticleLookupState>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, hasRole, openEdit, confirmDelete, confirmPublish, confirmHide]);
}

const useEditArticle = () => {
  const push = useNavigate();

  return useCallback(
    (id: string) => {
      push(`/admin/articles/${id}/edit`);
    },
    [push],
  );
};

export function ArticlesListTabPanel() {
  const { deleteAction } = useDeleteArticleAction();
  const { publishAction, hideAction } = useUpdateArticleStatusAction();
  const hasRole = useUserContextSelector((x) => x.hasRole);
  const onEdit = useEditArticle();
  const canModify = hasRole('BFP_ADMIN');
  const { setTotal, paging, params } = useDefaultTablePaging();
  const [searchString, setSearchString] = useState<string>();
  const { sorting, onSortingChange } =
    useSorting<ArticleLookupState>(DefaultNumberSorting);
  const [statuses, setStatuses] = useState<ArticleStatus[]>();
  const { data: paged, isFetching } = useSearchArticlesQuery({
    paging: params,
    statuses: statuses && statuses.length > 0 ? statuses : undefined,
    searchString,
    sorting,
  });

  const articles = useMemo(() => {
    if (paged) {
      setTotal(paged.total);
    }
    return paged?.items;
  }, [paged, setTotal]);

  const columns = useColumns(onEdit, deleteAction, publishAction, hideAction);
  const { t } = useTranslation();

  return (
    <TabPanel min="auto">
      <TabPanel.Title>{t('articles.title')}</TabPanel.Title>
      <TabPanel.Actions>{canModify && <AddArticleButton />}</TabPanel.Actions>
      <TabPanel.Body>
        <Table<ArticleLookupState>
          name="Articles"
          loading={isFetching}
          columns={columns}
          dataSource={articles}
          rowKey={(row) => row.id}
          pagination={paging}
          hideResetFiltersAction
          rightActions={
            <ArticlesSearchBar onSearch={setSearchString} className="mb-4" />
          }
          onChange={(_, filters, sorting) => {
            onSortingChange(sorting);
            setStatuses(filters.status as ArticleStatus[]);
          }}
        />
      </TabPanel.Body>
    </TabPanel>
  );
}
